<template>
  <div class="survey-page">
    <van-nav-bar title="问卷调查" />
    <div class="button-container">
      <!-- 病历详情 -->
      <button class="btn btn-bingli" @click="showBingliDetail" style="text-align: center;font-size: 20px;">
        <i class="el-icon-s-order"></i>
        <span>病例详情</span>
        <span v-if="bingliCompleted" class="completed-tag">已完成</span>
      </button>

      <!-- 基本信息 -->
      <button class="btn btn-xinxi" @click="showJibenXinxi" style="text-align: center;font-size: 20px;">
        <i class="el-icon-s-custom"></i>
        <span>基本信息</span>
        <span v-if="xinxiCompleted" class="completed-tag">已完成</span>
      </button>

      <!-- 文件上传 -->
      <div class="wenjian">
        <fieldset class="mt-16">
          <legend>证明文件上传</legend>
          <!-- 上传组件：适配Element Plus提示，优化状态绑定 -->
          <van-uploader :after-read="handleAfterRead" :before-read="beforeFileRead"
            :disabled="isUploading || isSubmitting" v-model="uploadFiles" :show-upload="true" upload-text="选择图片"
            preview-size="100" :deletable="true" @delete="handleFileDelete" accept="image/*">
            <van-button icon="plus" type="default" :loading="isUploading">
              {{ isUploading ? "上传中..." : "上传/更换文件" }}
            </van-button>
          </van-uploader>

          <!-- 上传提示 -->
          <p class="upload-hint">
            支持png/jpg图片格式，最大5MB
          </p>

          <!-- 上传状态反馈（Element Plus风格提示） -->
          <div v-if="isUploadSuccess" class="upload-success-feedback">
            <el-icon class="success-icon">
              <Check />
            </el-icon>
            <span class="success-text">文件上传成功！</span>
            <!-- 成功后显示文件预览 -->
            <el-image v-if="uploadFiles[0]?.url" :src="uploadFiles[0].url" class="preview-image" lazy alt="上传文件预览"
              :preview-src-list="[uploadFiles[0].url]" />
          </div>
        </fieldset>
      </div>

      <div class="shuoming">
        <p class="solid" style="color: brown;">上传证明文件（三选一即可）：<br>
          1.疾病相关门诊病历。【包含医生诊断】<br>
          2.出院证明。<br>
          3.疾病检测结果或化验单。【就诊检测报告】</p>
        <p class="solid" style="color: red">温馨提示:<br>
          审核时间24小时内，节假日顺延！</p>

        <!-- 查看复课证明 -->
        <button class="btn btn-zhengming" @click="showFukezhengming" style="text-align:center;font-size: 20px;">
          <i class="el-icon-magic-stick"></i>
          <span>生成复课证明</span>
          <span v-if="zhengmingCompleted" class="completed-tag">已生成</span>
        </button>
      </div>

      <div class="submit-btn-container">
        <van-button type="primary" class="submit-btn" @click="submitForm"
          :disabled="isUploading || isSubmitting || !canSubmit">
          {{ isSubmitting ? "提交中..." : "保存" }}
        </van-button>
      </div>
      <div class="submit-btn-container">
        <!-- 新增退出登录按钮 -->
        <van-button type="default" class="logout-btn" @click="handleLogout" :disabled="isUploading || isSubmitting"
          style="margin-top: 12px;">
          退出登录
        </van-button>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import request from '@/utils/request';
import { Toast } from 'vant';
import { Check } from '@element-plus/icons-vue';
import { ElMessage, ElIcon, ElImage, ElLoading, ElMessageBox } from 'element-plus';
import { VanUploader, VanButton, VanImage as VantImage, VanNavBar } from 'vant';

const UPLOAD_API = '/api/questionnaires/uploadProof';
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ACCEPT_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];

// 新增：判断是否为iOS系统（精准匹配iPhone/iPad）
function isIOS() {
  const u = navigator.userAgent;
  return !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/) || u.includes('iPhone') || u.includes('iPad');
}

// 新增：判断是否为Android系统
function isAndroid() {
  const u = navigator.userAgent;
  return u.indexOf('Android') > -1 || u.indexOf('Adr') > -1 || u.includes('Linux; Android');
}

function escapeHtml(unsafe) {
  if (typeof unsafe !== 'string') {
    return unsafe;
  }
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function isValidUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
}

// 核心修复：iOS 文件流下载函数（优化格式标识和下载兼容性）
function downloadBlobFile(blob, filename) {
  try {
    // 修复：避免重复添加 .doc 后缀（如果已存在则不拼接）
    const fileExt = filename.split('.').pop().toLowerCase();
    const finalFilename = ['doc', 'docx'].includes(fileExt)
      ? filename
      : `${filename}.docx`; // 优先用 docx 格式（兼容性更好）

    // 创建临时下载链接
    const url = URL.createObjectURL(blob);

    // 创建 a 标签触发下载（添加 download 属性强制指定文件名和格式）
    const link = document.createElement('a');
    link.href = url;
    link.download = finalFilename; // 强制指定完整文件名+后缀
    link.rel = 'noopener noreferrer';
    link.style.display = 'none';
    document.body.appendChild(link);

    // 兼容 iOS 微信：触发点击时添加延迟，确保文件流完全加载
    setTimeout(() => {
      if (link.click) {
        link.click();
      } else {
        const event = document.createEvent('MouseEvents');
        event.initEvent('click', true, true);
        link.dispatchEvent(event);
      }

      // 延迟清理（延长至 2000ms，避免文件未下载完就释放）
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url); // 释放内存
      }, 2000);
    }, 300);

    return true;
  } catch (error) {
    console.error('iOS下载word文件失败:', error);
    return false;
  }
}

// Android微信跳转浏览器函数
function openUrlInBrowser(url) {
  try {
    // 尝试直接打开（部分浏览器支持）
    window.open(url, '_blank');

    // 备用方案：通过a标签触发
    setTimeout(() => {
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }, 500);
  } catch (error) {
    console.error('打开浏览器失败:', error);
    throw new Error('无法自动打开浏览器，请手动复制链接在浏览器中打开');
  }
}

// iOS微信浏览器专用提示
function showIOSWechatTip() {
  return ElMessageBox.confirm(
    `<div style="text-align:left;line-height:1.8;">
      微信环境下iOS系统将直接下载复课证明（Word格式）：
      <br><br>
      1. 点击「确认下载」后将自动开始下载
      <br>
      2. 下载完成后微信可能促使文件损坏，请提前下载WPS或Word软件
      <br>
      3. 文件损坏时或盖章显示不全时请点击右上角"..."使用WPS或word应用打开
      <br>
      4. 若未自动下载，请检查微信下载权限
      <br><br>
      <span style="color:red;">⚠️ 重要提示：微信提示损坏时或盖章显示不全时直接忽略，用WPS打开即可正常查看，建议下载后及时保存</span>
    </div>`,
    'iOS微信下载提示',
    {
      confirmButtonText: '确认下载',
      cancelButtonText: '取消',
      type: 'info',
      closeOnClickModal: false,
      closeOnPressEscape: false,
      dangerouslyUseHTMLString: true,
      width: '90%'
    }
  );
}

export default defineComponent({
  name: 'Survey',
  components: {
    ElIcon,
    Check,
    ElImage,
    VanUploader,
    VanButton,
    VantImage,
    VanNavBar
  },
  data() {
    return {
      uploadFiles: [],
      formData: { filePath: null },
      bingliCompleted: false,
      xinxiCompleted: false,
      zhengmingCompleted: false,
      isUploading: false,
      isSubmitting: false,
      queChildrenSfz: '', // 存储学生身份证号
      userInfo: null,
      missingFields: [],
      isUploadSuccess: false
    };
  },
  computed: {
    canSubmit() {
      const basicReady = this.bingliCompleted && this.xinxiCompleted;
      const dataComplete = this.userInfo && this.checkAllRequiredFields();
      const sfzReady = !!this.queChildrenSfz;
      const uploadReady = this.isUploadSuccess;
      return basicReady && dataComplete && sfzReady && uploadReady;
    }
  },
  mounted() {
    this.initUploadedFile();
    this.initPageData();
    this.zhengmingCompleted = localStorage.getItem('zhengmingCompleted') === 'true';
  },

  methods: {
    isWeixinEnv() {
      return /MicroMessenger/i.test(navigator.userAgent);
    },

    getAuthToken() {
      return localStorage.getItem('userToken') || '';
    },

    initUploadedFile() {
      const savedFilePath = localStorage.getItem('uploadedFilePath');
      const savedFileName = localStorage.getItem('uploadedFileName');
      if (savedFilePath && savedFileName) {
        this.uploadFiles = [{ url: savedFilePath, name: savedFileName }];
        this.formData.filePath = savedFilePath;
        this.isUploadSuccess = true;
      }
    },

    handleAfterRead(file) {
      return this.afterFileRead(file);
    },

    beforeFileRead(file) {
      console.log('文件上传前校验：', file);
      const uploadFile = file.raw || file.originalFileObj || file.file || file;

      const fileType = uploadFile.type || file.raw?.type || '';
      const fileExt = uploadFile.name ? uploadFile.name.split('.').pop().toLowerCase() : '';
      const validExts = ['png', 'jpg', 'jpeg'];

      if (!ACCEPT_TYPES.includes(fileType) && !validExts.includes(fileExt)) {
        ElMessage.error(`仅支持${validExts.join('/')}格式图片`);
        return false;
      }

      if (uploadFile.size > MAX_FILE_SIZE) {
        ElMessage.error(`文件大小不能超过${MAX_FILE_SIZE / 1024 / 1024}MB`);
        return false;
      }

      if (!this.queChildrenSfz) {
        ElMessage.error('请先完善基本信息，获取学生身份证号后再上传文件');
        return false;
      }

      if (!this.bingliCompleted || !this.xinxiCompleted) {
        ElMessage.error('请先完成病例详情和基本信息填写后再上传文件');
        return false;
      }

      if (this.uploadFiles.length >= 1) {
        return new Promise((resolve) => {
          ElMessageBox.confirm(
            '已存在上传文件，是否替换当前文件？',
            '提示',
            {
              confirmButtonText: '替换',
              cancelButtonText: '取消',
              type: 'info'
            }
          ).then(() => {
            resolve(true);
          }).catch(() => {
            ElMessage.info('已取消文件上传');
            resolve(false);
          });
        });
      }

      return true;
    },

    handleLogout() {
      this.$confirm('确定要退出登录吗？未保存的数据可能会丢失', '退出提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        localStorage.removeItem('userToken');
        localStorage.removeItem('userInfo');
        localStorage.removeItem('bingliCompleted');
        localStorage.removeItem('xinxiCompleted');
        localStorage.removeItem('zhengmingCompleted');
        localStorage.removeItem('uploadedFilePath');
        localStorage.removeItem('apiResponseData');
        this.$store.commit('clearUserRoles');
        this.$router.push('/denglu').then(() => {
          this.$message.success('退出登录成功');
        });
      }).catch(() => {
        this.$message.info('已取消退出');
      });
    },

    initPageData() {
      this.bingliCompleted = localStorage.getItem('bingliCompleted') === 'true';
      this.xinxiCompleted = localStorage.getItem('xinxiCompleted') === 'true';
      this.loadUserInfo();
      this.syncUploadedFilePath();
      this.checkAllRequiredFields();
    },

    loadUserInfo() {
      const userInfoStr = localStorage.getItem('userInfo');
      if (userInfoStr) {
        try {
          this.userInfo = JSON.parse(userInfoStr);
          this.queChildrenSfz = this.userInfo.queChildrenSfz || ''; // 从本地存储获取身份证号
        } catch (e) {
          console.error('解析userInfo失败:', e);
          Toast.warning('加载用户信息失败，请重新登录');
          this.userInfo = null;
        }
      } else {
        Toast.warning('未检测到登录信息，请重新登录');
        setTimeout(() => {
          this.$router.push('/');
        }, 1500);
        this.userInfo = null;
      }
    },

    syncUploadedFilePath() {
      const cachedFilePath = localStorage.getItem('uploadedFilePath') || this.userInfo?.queChildrenFile;
      if (cachedFilePath) {
        this.formData.filePath = cachedFilePath;
        this.isUploadSuccess = true;
      } else {
        this.isUploadSuccess = false;
      }
    },

    checkAllRequiredFields() {
      if (!this.userInfo) return false;
      const requiredFields = {
        queParentName: '家长姓名',
        quePhone: '家长联系电话',
        queHospital: '就诊医院',
        queDisease: '患病名称',
        queFallTime: '发病日期',
        queDiagnosisTime: '诊断日期',
        queSymptoms: '主要症状状态',
        queChildrenSfz: '学生身份证号',
        queChildrenName: '学生姓名',
        queChildrenGender: '学生性别',
        queChildrenAddress: '现居地址',
        queChildrenRelationship: '亲属关系',
        queChildrenSchool: '就读学校',
        queChildrenGrade: '年级',
        queChildrenClass: '班级'
      };
      this.missingFields = [];
      Object.entries(requiredFields).forEach(([key, desc]) => {
        const value = this.userInfo[key];
        if (value === undefined || value === null || value === '') {
          this.missingFields.push(desc);
        }
      });
      const hasSymptomOther = this.userInfo.queSymptomsOther && this.userInfo.queSymptomsOther.trim() !== '';
      const hasSymptomTime = this.userInfo.queSymptomsTime && this.userInfo.queSymptomsTime.trim() !== '';
      if (!hasSymptomOther && !hasSymptomTime) {
        this.missingFields.push('症状详情/症状消失时间（二选一）');
      }
      return this.missingFields.length === 0;
    },

    showBingliDetail() {
      this.$router.push('/bingli').then(() => {
        setTimeout(() => {
          this.initPageData();
        }, 500);
      });
    },

    showJibenXinxi() {
      this.$router.push('/xinxi').then(() => {
        setTimeout(() => {
          this.initPageData();
        }, 500);
      });
    },

    // 核心优化：生成复课证明（严格区分iOS/Android接口和逻辑）
    async showFukezhengming() {
      if (!this.queChildrenSfz) {
        ElMessage.error('未获取到学生身份证号，无法生成证明');
        return;
      }

      const loadingInstance = ElLoading.service({
        text: '正在查询审核记录，请稍候...',
        lock: false,
        background: 'rgba(255, 255, 255, 0.7)'
      });

      try {
        // 1. 查询审核记录
        const reviewListResponse = await request({
          url: '/api/review/listByChildrenSfz',
          method: 'get',
          params: { childrenSfz: encodeURIComponent(this.queChildrenSfz) }
        });

        const { code: listCode, data: reviewList, message: listMessage } = reviewListResponse;
        if (listCode !== 200 || !Array.isArray(reviewList)) {
          throw new Error(`查询审核记录失败：${listMessage || '接口返回异常'}`);
        }

        loadingInstance.close();
        console.log('后端返回的原始审核记录：', reviewList);

        if (reviewList.length === 0) {
          await ElMessageBox.alert(
            '请先填写病例和基本信息并提交，等待医生审核通过后再生成复课证明！',
            '温馨提示',
            {
              confirmButtonText: 'OK',
              type: 'info'
            }
          );
          return;
        }

        // 2. 筛选已通过的审核记录
        const passedReviews = reviewList.filter(review => parseInt(review.reStatus, 10) === 0);
        if (passedReviews.length === 0) {
          await ElMessageBox.alert(
            '暂无已通过的审核记录，请等待医生审核通过后再试！',
            '温馨提示',
            {
              confirmButtonText: 'OK',
              type: 'info'
            }
          );
          return;
        }

        // 3. 构建审核记录选择框
        const getStatusConfig = (statusCode, rejectReason = '') => {
          const code = parseInt(statusCode, 10);
          const configs = {
            0: {
              text: '已通过',
              icon: 'el-icon-check',
              tooltip: '审核已通过，可以生成复课证明',
              className: 'status-tag status-tag--success',
              enabled: true,
              reason: ''
            },
            1: {
              text: '审核中',
              icon: 'el-icon-time',
              tooltip: '正在审核中，请耐心等候',
              className: 'status-tag status-tag--pending',
              enabled: false,
              reason: ''
            },
            2: {
              text: '已驳回',
              icon: 'el-icon-circle-close',
              tooltip: `审核已驳回，驳回原因为：${rejectReason || '暂无原因'}`,
              className: 'status-tag status-tag--rejected',
              enabled: false,
              reason: rejectReason
            }
          };
          return configs[code] || {
            text: `未知状态(${statusCode})`,
            icon: 'el-icon-question',
            tooltip: '状态异常，请联系管理员',
            className: 'status-tag status-tag--unknown',
            enabled: false,
            reason: ''
          };
        };

        const formatDate = (dateStr) => {
          if (!dateStr) return '未知日期';
          const date = new Date(dateStr);
          return isNaN(date.getTime())
            ? dateStr
            : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        };

        const styleSheet = `
      <style>
        .review-card { border: 1px solid #ebeef5; border-radius: 8px; padding: 15px; margin-bottom: 12px; transition: box-shadow 0.3s ease; }
        .review-card:hover { box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.08); }
        .review-card__header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
        .review-card__title { font-weight: 500; color: #333; }
        .review-card__content { color: #666; font-size: 13px; line-height: 1.6; }
        .review-card__meta { margin-bottom: 4px; }
        .review-card__meta-label { color: #999; margin-right: 4px; }
        .review-card__hint { margin-top: 8px; padding: 8px; border-radius: 4px; font-size: 12px; line-height: 1.5; }
        .hint--pending { background-color: #f0f8fb; color: #027aff; border: 1px solid #dbeafe; }
        .hint--rejected { background-color: #fef0f0; color: #d93f0b; border: 1px solid #fcc367; }

        .status-tag {
          display: inline-flex;
          align-items: center;
          padding: 3px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
        }
        .status-tag i { margin-right: 4px; font-size: 14px; }
        .status-tag--success { background-color: #f0f9f0; color: #099753; border: 1px solid #d1fadf; }
        .status-tag--pending { background-color: #f0f8fb; color: #027aff; border: 1px solid #dbeafe; }
        .status-tag--rejected { background-color: #fef0f0; color: #d93f0b; border: 1px solid #fcc367; }
        .status-tag--unknown { background-color: #f8f8f8; color: #666; border: 1px solid #e0e0e0; }
        
        .reject-reason { color: #d93f0b; font-weight: 500; }
        .radio-disabled-label { color: #999; cursor: not-allowed; }
      </style>
    `;

        let messageHtml = styleSheet + `<div style="max-height: 400px; overflow-y: auto;">`;

        reviewList.forEach((review, index) => {
          const statusConfig = getStatusConfig(review.reStatus, review.reOpinionOther);

          const passedHint = statusConfig.enabled
            ? `<div class="review-card__hint" style="background-color: #f0f9f0; color: #099753; border: 1px solid #d1fadf;">
            <i class="el-icon-info" style="margin-right:4px;"></i>此记录已通过审核，可生成复课证明。
          </div>`
            : '';

          const pendingHint = statusConfig.text === '审核中'
            ? `<div class="review-card__hint hint--pending">
            <i class="el-icon-info" style="margin-right:4px;"></i>正在审核中，请耐心等候。
          </div>`
            : '';

          const rejectedHint = statusConfig.text === '已驳回'
            ? `<div class="review-card__hint hint--rejected">
            <i class="el-icon-warning" style="margin-right:4px;"></i>审核已驳回，驳回原因为：<span class="reject-reason">${escapeHtml(statusConfig.reason || '暂无原因')}</span>，请重新填写信息并提交。
          </div>`
            : '';

          messageHtml += `
        <div class="review-card">
          <div class="review-card__header">
            <span class="review-card__title">审核记录 #${index + 1}</span>
            <span class="${statusConfig.className}" title="${statusConfig.tooltip}">
              <i class="${statusConfig.icon}"></i>
              ${statusConfig.text}
            </span>
          </div>
          <div class="review-card__content">
            <div class="review-card__meta"><span class="review-card__meta-label">审核ID：</span>${review.reId || '无'}</div>
            <div class="review-card__meta"><span class="review-card__meta-label">提交日期：</span>${formatDate(review.createTime || review.submitDate)}</div>
            <div class="review-card__meta"><span class="review-card__meta-label">患病名称：</span>${review.reDisease || '未填写'}</div>
            ${passedHint}${pendingHint}${rejectedHint}
          </div>
          <div style="margin-top: 10px;">
            <input type="radio" name="reviewSelect" id="review_${index}" value="${index}" 
              ${statusConfig.enabled ? '' : 'disabled'} 
              style="margin-right: 6px; cursor: ${statusConfig.enabled ? 'pointer' : 'not-allowed'};">
            <label for="review_${index}" style="cursor: ${statusConfig.enabled ? 'pointer' : 'not-allowed'};">
              ${statusConfig.enabled ? '此记录可以生成复课证明❥(^_-)' : '审核未通过不允许生成复课证明(＞人＜；)'}
            </label>
          </div>
        </div>
      `;
        });

        messageHtml += `</div>`;

        // 4. 弹出选择框
        await ElMessageBox({
          title: '请选择一条审核信息生成复课证明',
          message: messageHtml,
          type: 'info',
          showCancelButton: true,
          confirmButtonText: '生成证明',
          cancelButtonText: '取消',
          dangerouslyUseHTMLString: true,
          beforeClose: async (action, instance, done) => {
            if (action === 'confirm') {
              const selectedRadio = document.querySelector('input[name="reviewSelect"]:checked');
              if (!selectedRadio) {
                ElMessage.warning('请选择一条“已通过”的审核记录');
                return;
              }

              const selectedIndex = parseInt(selectedRadio.value);
              const selectedReview = reviewList[selectedIndex];
              const selectedReId = selectedReview.reId; // 存储选中的审核ID
              const childrenSfz = this.queChildrenSfz; // 存储的身份证号
              const authToken = this.getAuthToken();
              // 生成word格式文件名
              const filename = `复课证明_${childrenSfz}_${selectedReId}`;

              // 判定当前设备系统
              const currentIsIOS = isIOS();
              const currentIsAndroid = isAndroid();
              const currentIsWechat = this.isWeixinEnv();
              console.log(`设备类型：${currentIsIOS ? 'iOS' : currentIsAndroid ? 'Android' : '其他'}，环境：${currentIsWechat ? '微信浏览器' : '普通浏览器'}`);

              const generateLoading = ElLoading.service({
                text: '正在生成复课证明，请稍候...',
                lock: false,
                background: 'rgba(255, 255, 255, 0.7)'
              });

              try {
                // ========== iOS系统逻辑 ==========
                if (currentIsIOS) {
                  console.log('iOS系统 - 调用接口：/api/review/generateProof');
                  console.log('请求参数：sfz=', childrenSfz, ', reId=', selectedReId);

                  // iOS微信环境先显示提示
                  if (currentIsWechat) {
                    await showIOSWechatTip();
                  }

                  // 调用iOS专用接口（返回文件流）
                  const generateResponse = await request({
                    url: '/api/review/generateProof', // iOS专用接口
                    method: 'post',
                    params: {
                      sfz: encodeURIComponent(childrenSfz), // 身份证号参数名：sfz
                      reId: encodeURIComponent(selectedReId) // 审核ID参数名：reId
                    },
                    headers: {
                      ...(authToken && { 'authorization': `Bearer ${authToken}` }),
                      'Content-Type': 'application/json'
                    },
                    timeout: 30000,
                    responseType: 'blob' // 关键：指定响应类型为blob（文件流）
                  });

                  generateLoading.close();

                  // 验证响应数据
                  if (!generateResponse.data || !(generateResponse.data instanceof Blob)) {
                    throw new Error('接口返回数据不是有效的文件流');
                  }

                  // 处理word格式文件流（指定MIME类型）
                  const blob = new Blob([generateResponse.data], {
                    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document; charset=utf-8' // docx格式
                  });

                  // 触发微信直接下载
                  const downloadSuccess = downloadBlobFile(blob, filename);

                  if (downloadSuccess) {
                    ElMessage.success('复课证明生成成功！微信可能提示"文件损坏"，请忽略并使用 WPS/Word 打开');
                    this.zhengmingCompleted = true;
                    localStorage.setItem('zhengmingCompleted', 'true');
                  } else {
                    throw new Error('文件下载触发失败，请检查微信权限');
                  }
                }
                // ========== Android系统逻辑 ==========
                else if (currentIsAndroid) {
                  console.log('Android系统 - 调用接口：/api/review/generateProofUrl');
                  console.log('请求参数：reChildrenSfz=', childrenSfz, ', reId=', selectedReId);

                  // 微信环境必须提示跳转浏览器
                  if (currentIsWechat) {
                    try {
                      await ElMessageBox.confirm(
                        `<div style="text-align:left;line-height:1.8;">
                          微信环境无法直接下载Word文件，请前往浏览器：
                          <br><br>
                          1. 点击「前往浏览器」后选择手机默认浏览器
                          <br>
                          2. 浏览器将自动下载复课证明
                          <br>
                          3. 若未自动下载，请复制链接手动打开
                          <br><br>
                          <span style="color:red;">⚠️ 注意：请允许浏览器弹出新窗口</span>
                        </div>`,
                        '微信环境下载提示',
                        {
                          confirmButtonText: '前往浏览器',
                          cancelButtonText: '取消',
                          type: 'info',
                          closeOnClickModal: false,
                          closeOnPressEscape: false,
                          dangerouslyUseHTMLString: true,
                          width: '90%'
                        }
                      );
                    } catch (error) {
                      ElMessage.info('已取消生成复课证明');
                      done();
                      return;
                    }
                  }

                  // 调用Android专用接口（返回下载链接）
                  const generateResponse = await request({
                    url: '/api/review/generateProofUrl', // Android专用接口
                    method: 'post',
                    params: {
                      reChildrenSfz: encodeURIComponent(childrenSfz), // 身份证号参数名：reChildrenSfz
                      reId: encodeURIComponent(selectedReId) // 审核ID参数名：reId
                    },
                    headers: {
                      ...(authToken && { 'authorization': `Bearer ${authToken}` })
                    },
                    timeout: 30000
                  });

                  generateLoading.close();

                  // 处理接口返回结果（下载链接在message字段中）
                  const { code, message: downloadUrl, data } = generateResponse;
                  if (code !== 200) {
                    throw new Error(`接口调用失败：${message || '服务器异常'}`);
                  }

                  // 提取下载链接（优先取message字段）
                  let finalDownloadUrl = downloadUrl || '';
                  if (!isValidUrl(finalDownloadUrl)) {
                    // 备用：从data中提取
                    finalDownloadUrl = data?.downloadUrl || data?.url || '';
                  }

                  if (!isValidUrl(finalDownloadUrl)) {
                    throw new Error('未从接口message字段获取到有效的下载链接');
                  }

                  console.log('Android获取到的下载链接：', finalDownloadUrl);

                  // 打开浏览器下载
                  openUrlInBrowser(finalDownloadUrl);

                  ElMessage.success('已跳转至浏览器，正在下载复课证明！');
                  this.zhengmingCompleted = true;
                  localStorage.setItem('zhengmingCompleted', 'true');
                }
                // ========== 其他设备 ==========
                else {
                  generateLoading.close();
                  throw new Error('暂不支持当前设备系统，请使用iOS或Android设备');
                }

                done();
              } catch (generateError) {
                generateLoading.close();
                console.error('生成复课证明失败:', generateError);

                // 针对性错误提示
                if (currentIsIOS) {
                  await ElMessageBox.alert(
                    `<div style="text-align:left;line-height:1.8;">
                      iOS系统下载失败：${generateError.message || '服务器异常'}
                      <br><br>
                      操作建议：
                      <br>1. 检查微信下载权限（设置-通用-文件下载）
                      <br>2. 确保网络连接稳定
                      <br>3. 重新尝试生成
                    </div>`,
                    '下载失败提示',
                    {
                      confirmButtonText: '已了解',
                      type: 'warning',
                      dangerouslyUseHTMLString: true,
                      width: '90%'
                    }
                  );
                } else if (currentIsAndroid) {
                  await ElMessageBox.alert(
                    `<div style="text-align:left;line-height:1.8;">
                      Android系统下载失败：${generateError.message || '服务器异常'}
                      <br><br>
                      手动下载方案：
                      <br>1. 复制以下链接：
                      <br><code style="color:blue;">${finalDownloadUrl || '无有效链接'}</code>
                      <br>2. 打开浏览器粘贴链接访问
                    </div>`,
                    '下载失败提示',
                    {
                      confirmButtonText: '复制链接',
                      cancelButtonText: '取消',
                      type: 'warning',
                      dangerouslyUseHTMLString: true,
                      width: '90%',
                      beforeClose: (action) => {
                        if (action === 'confirm' && finalDownloadUrl) {
                          navigator.clipboard.writeText(finalDownloadUrl).then(() => {
                            ElMessage.success('链接已复制到剪贴板');
                          }).catch(() => {
                            ElMessage.warning('复制失败，请手动复制');
                          });
                        }
                      }
                    }
                  );
                }
              }
            } else {
              done();
            }
          }
        });

      } catch (error) {
        loadingInstance.close();
        console.error('生成复课证明异常:', error);

        if (error === 'cancel') {
          ElMessage.info('已取消生成复课证明');
          return;
        }

        await ElMessageBox.alert(
          '操作过程中发生错误，请稍后重试。',
          '错误提示',
          {
            confirmButtonText: 'OK',
            type: 'error'
          }
        );
      }
    },

    async afterFileRead(file) {
      console.log('文件读取完成，开始上传：', file);
      console.log('用户身份证号：', this.queChildrenSfz);
      if (this.uploadFiles.length > 0) {
        console.log('发现已有文件，将自动覆盖');
        this.resetUploadState();
      }
      const validateResult = this.validateUpload(file);
      if (!validateResult.valid) {
        ElMessage.error(validateResult.msg);
        return;
      }
      const uploadFile = validateResult.file;
      this.isUploading = true;
      this.isUploadSuccess = false;
      try {
        const formData = new FormData();
        formData.append('file', uploadFile);
        formData.append('psId', this.queChildrenSfz);
        const response = await request({
          url: UPLOAD_API,
          method: 'POST',
          data: formData,
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000,
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded / (progressEvent.total || 1)) * 100);
            console.log(`上传进度：${percent}%`);
          }
        });
        this.handleUploadSuccess(response, uploadFile);
      } catch (error) {
        this.handleUploadError(error);
      } finally {
        this.isUploading = false;
      }
    },

    validateUpload(file) {
      if (!this.queChildrenSfz) {
        return { valid: false, msg: '缺少用户身份证号信息，无法上传文件' };
      }
      const uploadFile = file.raw || file.originalFileObj || file.file || file;
      if (!uploadFile || !uploadFile.name || !uploadFile.size) {
        return { valid: false, msg: '文件对象异常，请重新选择文件' };
      }
      if (uploadFile.size > MAX_FILE_SIZE) {
        return { valid: false, msg: `文件大小不能超过${MAX_FILE_SIZE / 1024 / 1024}MB` };
      }
      const fileType = uploadFile.type || file.raw?.type || '';
      if (!ACCEPT_TYPES.includes(fileType)) {
        const supportTypes = ACCEPT_TYPES.map(t => t.split('/')[1].toUpperCase()).join('/');
        return { valid: false, msg: `仅支持${supportTypes}格式图片` };
      }
      return { valid: true, file: uploadFile };
    },

    handleUploadSuccess(response, uploadFile) {
      console.log('上传成功，响应数据：', response);
      const { data, filePath, url, message } = response || {};
      const fileUrl = (data || filePath || url || '').trim();
      if (fileUrl) {
        ElMessage.success({ message: message || '文件上传成功！', duration: 2000 });
        this.formData.filePath = fileUrl;
        this.isUploadSuccess = true;
        this.uploadFiles = [{ url: fileUrl, name: uploadFile.name }];
        localStorage.setItem('uploadedFilePath', fileUrl);
        localStorage.setItem('uploadedFileName', uploadFile.name);
        if (this.userInfo) {
          this.userInfo.queChildrenFile = fileUrl;
          localStorage.setItem('userInfo', JSON.stringify(this.userInfo));
        }
      } else {
        throw new Error(message || '上传成功，但未获取到有效文件路径');
      }
    },

    handleUploadError(error) {
      console.error('上传失败：', error);
      ElMessage.error({ message: error.message || '文件上传失败，请重试', duration: 3000 });
      this.resetUploadState();
    },

    resetUploadState() {
      this.formData.filePath = null;
      this.isUploadSuccess = false;
      this.uploadFiles = [];
      localStorage.removeItem('uploadedFilePath');
      localStorage.removeItem('uploadedFileName');
      if (this.userInfo) {
        this.userInfo.queChildrenFile = '';
        localStorage.setItem('userInfo', JSON.stringify(this.userInfo));
      }
    },

    getCurrentTime() {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hour = String(date.getHours()).padStart(2, '0');
      const minute = String(date.getMinutes()).padStart(2, '0');
      const second = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
    },

    async submitForm() {
      // 1. 步骤完成校验
      const uncompletedSteps = [];
      if (!this.bingliCompleted) uncompletedSteps.push('病例详情');
      if (!this.xinxiCompleted) uncompletedSteps.push('基本信息');
      if (uncompletedSteps.length > 0) {
        ElMessage.error({ message: `请先完成以下步骤：${uncompletedSteps.join('、')}`, duration: 3000 });
        return;
      }

      // 2. 字段完整性校验
      const isDataComplete = this.checkAllRequiredFields();
      if (!isDataComplete) {
        ElMessage.error({ message: `数据不完整，请补充以下字段：${this.missingFields.join('、')}`, duration: 3000 });
        return;
      }

      // 3. 用户信息校验
      if (!this.userInfo || !this.queChildrenSfz) {
        ElMessage.error({ message: '用户信息缺失，请重新登录', duration: 3000 });
        setTimeout(() => this.$router.push('/'), 1500);
        return;
      }

      this.isSubmitting = true;
      try {

        // 4. 查询审核记录 
        console.log('步骤4: 查询审核记录...');
        const queryRResponse = await request({
          url: '/api/review/listByChildrenSfz',
          method: 'get',
          params: { childrenSfz: encodeURIComponent(this.queChildrenSfz) }
        });
        const isRSuccess = queryRResponse.code === 200 || queryRResponse.success === true;
        const rData = isRSuccess ? queryRResponse.data : [];
        const hasReviewRecord = Array.isArray(rData) && rData.length > 0;

        // 5. 根据审核记录存在性，决定是直接新增还是弹窗选择
        if (!hasReviewRecord) {
          console.log('未查询到审核记录，将直接执行新增操作。');

          // 5.1. 查询问卷记录 (在需要时才查询)
          console.log('步骤5.1: 查询问卷记录...');
          const queryQResponse = await request({
            url: '/api/questionnaires/getByChildrenSfz',
            method: 'get',
            params: { childrenSfz: encodeURIComponent(this.queChildrenSfz) }
          });
          const isQSuccess = queryQResponse.code === 200 || queryQResponse.success === true;
          const qData = isQSuccess ? queryQResponse.data : null;
          const qExistingRecord = Array.isArray(qData)
            ? qData.find(item => item.queChildrenSfz === this.queChildrenSfz)
            : (qData?.queChildrenSfz === this.queChildrenSfz ? qData : null);
          const storedSfz = qExistingRecord?.queChildrenSfz || '';
          const storedQueId = qExistingRecord?.queId || this.userInfo.queId || '';

          // 5.2. 校验 queId
          if (!storedQueId) throw new Error('缺少queId，无法继续提交');

          // 5.3. 更新/新增问卷记录
          if (storedSfz) {
            console.log('步骤5.3: 更新问卷记录...');
            const addOrUpdateData = {
              queChildrenSfz: storedSfz,
              queId: storedQueId,
              queParentName: this.userInfo.queParentName,
              quePhone: this.userInfo.quePhone,
              queHospital: this.userInfo.queHospital,
              queDisease: this.userInfo.queDisease,
              queFallTime: this.userInfo.queFallTime,
              queDiagnosisTime: this.userInfo.queDiagnosisTime,
              queSymptoms: this.userInfo.queSymptoms,
              queSymptomsOther: this.userInfo.queSymptomsOther || '',
              queSymptomsTime: this.userInfo.queSymptomsTime || '',
              queChildrenName: this.userInfo.queChildrenName,
              queChildrenGender: this.userInfo.queChildrenGender,
              queChildrenAddress: this.userInfo.queChildrenAddress,
              queChildrenRelationship: this.userInfo.queChildrenRelationship || '',
              queChildrenSchool: this.userInfo.queChildrenSchool,
              queChildrenGrade: this.userInfo.queChildrenGrade,
              queChildrenClass: this.userInfo.queChildrenClass,
            };
            const addOrUpdateResponse = await request({
              url: '/api/questionnaires/addOrUpdate',
              method: 'post',
              data: addOrUpdateData
            });
            const isAddOrUpdateSuccess = addOrUpdateResponse.code === 200 || addOrUpdateResponse.success === true;
            if (!isAddOrUpdateSuccess) {
              throw new Error(`更新问卷失败：${addOrUpdateResponse.message || '服务器错误'}`);
            }
            localStorage.setItem('apiResponseData', JSON.stringify(addOrUpdateResponse.data || {}));
            const updatedUserInfo = { ...this.userInfo, queChildrenFile: this.formData.filePath || '' };
            this.userInfo = updatedUserInfo;
            localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo));
          }

          // 5.4. 直接新增审核记录
          console.log('步骤5.4: 直接新增审核记录...');
          const reviewAddData = {
            queId: storedQueId,
            reId: storedQueId,
            queParentName: this.userInfo.queParentName,
            quePhone: this.userInfo.quePhone,
            queHospital: this.userInfo.queHospital,
            queDisease: this.userInfo.queDisease,
            queFallTime: this.userInfo.queFallTime,
            queDiagnosisTime: this.userInfo.queDiagnosisTime,
            queSymptoms: this.userInfo.queSymptoms,
            queSymptomsOther: this.userInfo.queSymptomsOther || '',
            queSymptomsTime: this.userInfo.queSymptomsTime || '',
            queChildrenSfz: this.userInfo.queChildrenSfz,
            queChildrenName: this.userInfo.queChildrenName,
            queChildrenGender: this.userInfo.queChildrenGender,
            queChildrenAddress: this.userInfo.queChildrenAddress,
            queChildrenRelationship: this.userInfo.queChildrenRelationship || '',
            queChildrenSchool: this.userInfo.queChildrenSchool,
            queChildrenGrade: this.userInfo.queChildrenGrade,
            queChildrenClass: this.userInfo.queChildrenClass,
            queChildrenFile: this.formData.filePath || '',
            updateTime: this.getCurrentTime(),
            reChildrenFile: this.formData.filePath || '',
            exam202502: '1'
          };

          const reviewAddResponse = await request({
            url: '/api/review/add',
            method: 'post',
            data: reviewAddData,
            headers: { "content-type": "application/json" }
          });

          if (!(reviewAddResponse.code === 200 || reviewAddResponse.success === true)) {
            throw new Error(`新增审核记录失败：${reviewAddResponse.message || '服务器错误'}`);
          }
          ElMessage.success('提交成功，已新增一条审核记录，请等待审核');
        } else {
          console.log('查询到审核记录，将弹出选择框。');

          // 6. 查询问卷记录
          console.log('步骤6: 查询问卷记录...');
          const queryQResponse = await request({
            url: '/api/questionnaires/getByChildrenSfz',
            method: 'get',
            params: { childrenSfz: encodeURIComponent(this.queChildrenSfz) }
          });
          const isQSuccess = queryQResponse.code === 200 || queryQResponse.success === true;
          const qData = isQSuccess ? queryQResponse.data : null;
          const qExistingRecord = Array.isArray(qData)
            ? qData.find(item => item.queChildrenSfz === this.queChildrenSfz)
            : (qData?.queChildrenSfz === this.queChildrenSfz ? qData : null);
          const storedSfz = qExistingRecord?.queChildrenSfz || '';
          const storedQueId = qExistingRecord?.queId || this.userInfo.queId || '';

          // 7. 校验 queId
          if (!storedQueId) throw new Error('缺少queId，无法继续提交');

          // 8. 更新/新增问卷记录
          if (storedSfz) {
            console.log('步骤8: 更新问卷记录...');
            const addOrUpdateData = {
              queChildrenSfz: storedSfz,
              queId: storedQueId,
              queParentName: this.userInfo.queParentName,
              quePhone: this.userInfo.quePhone,
              queHospital: this.userInfo.queHospital,
              queDisease: this.userInfo.queDisease,
              queFallTime: this.userInfo.queFallTime,
              queDiagnosisTime: this.userInfo.queDiagnosisTime,
              queSymptoms: this.userInfo.queSymptoms,
              queSymptomsOther: this.userInfo.queSymptomsOther || '',
              queSymptomsTime: this.userInfo.queSymptomsTime || '',
              queChildrenName: this.userInfo.queChildrenName,
              queChildrenGender: this.userInfo.queChildrenGender,
              queChildrenAddress: this.userInfo.queChildrenAddress,
              queChildrenRelationship: this.userInfo.queChildrenRelationship || '',
              queChildrenSchool: this.userInfo.queChildrenSchool,
              queChildrenGrade: this.userInfo.queChildrenGrade,
              queChildrenClass: this.userInfo.queChildrenClass,
            };
            const addOrUpdateResponse = await request({
              url: '/api/questionnaires/addOrUpdate',
              method: 'post',
              data: addOrUpdateData
            });
            const isAddOrUpdateSuccess = addOrUpdateResponse.code === 200 || addOrUpdateResponse.success === true;
            if (!isAddOrUpdateSuccess) {
              throw new Error(`更新问卷失败：${addOrUpdateResponse.message || '服务器错误'}`);
            }
            localStorage.setItem('apiResponseData', JSON.stringify(addOrUpdateResponse.data || {}));
            const updatedUserInfo = { ...this.userInfo, queChildrenFile: this.formData.filePath || '' };
            this.userInfo = updatedUserInfo;
            localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo));
          }

          // 9. 弹窗选择并处理审核记录
          console.log('步骤9: 弹窗选择操作...');
          const existingReview = rData[0];
          const currentExamValue = existingReview.exam202502 || 0;
          const reviewReId = existingReview.reId;

          const action = await ElMessageBox.confirm(
            '检测到您已存在审核信息，是否新增一条审核记录？',
            '审核记录确认',
            {
              confirmButtonText: '新增',
              cancelButtonText: '更新',
              type: 'warning',
              showCancelButton: true,
              showClose: true,
              closeOnClickModal: false,
              closeOnPressEscape: false,
              distinguishCancelAndClose: true
            }
          ).then(() => {
            return 'confirm'; // 点击“新增”
          }).catch(error => {
            if (error === 'cancel') {
              return 'update'; // 点击“更新”
            } else {
              return 'close'; // 点击关闭或ESC
            }
          });

          if (action === 'confirm') {
            // ---- 新增审核记录 ----
            console.log('用户选择【新增】审核记录');
            const reviewAddData = {
              queId: storedQueId,
              reId: storedQueId,
              queParentName: this.userInfo.queParentName,
              quePhone: this.userInfo.quePhone,
              queHospital: this.userInfo.queHospital,
              queDisease: this.userInfo.queDisease,
              queFallTime: this.userInfo.queFallTime,
              queDiagnosisTime: this.userInfo.queDiagnosisTime,
              queSymptoms: this.userInfo.queSymptoms,
              queSymptomsOther: this.userInfo.queSymptomsOther || '',
              queSymptomsTime: this.userInfo.queSymptomsTime || '',
              queChildrenSfz: this.userInfo.queChildrenSfz,
              queChildrenName: this.userInfo.queChildrenName,
              queChildrenGender: this.userInfo.queChildrenGender,
              queChildrenAddress: this.userInfo.queChildrenAddress,
              queChildrenRelationship: this.userInfo.queChildrenRelationship || '',
              queChildrenSchool: this.userInfo.queChildrenSchool,
              queChildrenGrade: this.userInfo.queChildrenGrade,
              queChildrenClass: this.userInfo.queChildrenClass,
              queChildrenFile: this.formData.filePath || '',
              updateTime: this.getCurrentTime(),
              reChildrenFile: this.formData.filePath || '',
              exam202502: currentExamValue + 1
            };

            const reviewAddResponse = await request({
              url: '/api/review/add',
              method: 'post',
              data: reviewAddData,
              headers: { "content-type": "application/json" }
            });

            if (!(reviewAddResponse.code === 200 || reviewAddResponse.success === true)) {
              throw new Error(`新增审核记录失败：${reviewAddResponse.message || '服务器错误'}`);
            }
            ElMessage.success('提交成功，已新增一条审核记录，请等待审核');
          } else if (action === 'update') {
            // ---- 更新审核记录 ----
            console.log('用户选择【更新】审核记录');
            const reviewUpdateData = {
              reId: reviewReId,
              reParentName: this.userInfo.queParentName,
              rePhone: this.userInfo.quePhone,
              reHospital: this.userInfo.queHospital,
              reDisease: this.userInfo.queDisease,
              reFallTime: this.userInfo.queFallTime,
              reDiagnosisTime: this.userInfo.queDiagnosisTime,
              reSymptoms: this.userInfo.queSymptoms,
              reSymptomsOther: this.userInfo.queSymptomsOther || '',
              reSymptomsTime: this.userInfo.queSymptomsTime || '',
              reChildrenSfz: this.userInfo.queChildrenSfz,
              reChildrenName: this.userInfo.queChildrenName,
              reChildrenGender: this.userInfo.queChildrenGender,
              reChildrenAddress: this.userInfo.queChildrenAddress,
              reChildrenSchool: this.userInfo.queChildrenSchool,
              reChildrenGrade: this.userInfo.queChildrenGrade,
              reChildrenClass: this.userInfo.queChildrenClass,
              reStatus: 1,
              updateTime: this.getCurrentTime(),
              reChildrenFile: this.formData.filePath || ''
            };

            const reviewUpdateResponse = await request({
              url: '/api/review/update',
              method: 'put',
              data: reviewUpdateData,
              headers: { "content-type": "application/json" }
            });

            if (!(reviewUpdateResponse.code === 200 || reviewUpdateResponse.success === true)) {
              throw new Error(`更新审核记录失败：${reviewUpdateResponse.message || '服务器错误'}`);
            }
            ElMessage.success('提交成功，已更新现有审核记录，请等待审核');
          } else {
            // ---- 用户取消操作 ----
            console.log('用户取消了操作');
            ElMessage.info('已取消提交操作');
            this.isSubmitting = false; // 提前重置状态
            return;
          }
        }

        // 10. 提交成功后的跳转
        console.log('步骤10: 提交成功，准备跳转...');
        setTimeout(() => {
          this.$router.push('/');
        }, 2000);

      } catch (error) {
        console.error('提交流程失败:', error);
        ElMessage.error({ message: `提交失败：${error.message || '未知错误'}`, duration: 3000 });
      } finally {
        this.isSubmitting = false;
      }
    },

    handleFileDelete(file, index) {
      console.log('删除文件:', file, '索引:', index);
      this.uploadFiles.splice(index, 1);
      if (this.uploadFiles.length === 0) {
        this.resetUploadState();
      } else {
        this.formData.filePath = this.uploadFiles[0].url;
        localStorage.setItem('uploadedFilePath', this.formData.filePath);
        localStorage.setItem('uploadedFileName', this.uploadFiles[0].name);
      }
    }
  },
});
</script>

<style scoped>
/* 页面背景 */
.survey-page {
  background-color: #f5f5f5;
  min-height: 100vh;
  padding-bottom: 60px;
  /* 为底部按钮留出空间 */
}

/* 按钮组：采用Flex布局，垂直排列 */
.button-container {
  display: flex;
  flex-direction: column;
  /* 垂直排列 */
  align-items: center;
  /* 水平居中 */
  padding: 16px;
  box-sizing: border-box;
}

/* 通用按钮样式 */
.btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  /* 宽度占满90% */
  max-width: 400px;
  /* 最大宽度限制 */
  padding: 14px 12px;
  /* 上下内边距增加，手指点击区域更大 */
  margin-bottom: 12px;
  /* 按钮之间的间距 */
  border-radius: 8px;
  /* 圆角增大，更美观 */
  cursor: pointer;
  border: 1px solid transparent;
  color: #333;
  font-size: 16px;
  /* 字体稍大，便于阅读 */
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
  /* 去除移动端点击高亮 */
}

/* 图标样式 */
.btn i {
  font-size: 20px;
  margin-right: 8px;
  /* 图标与文字间距 */
}

/* 蓝色按钮样式 */
.btn-bingli,
.btn-xinxi {
  background-color: #bbdefb;
  border-color: #64b5f6;
}

/* 橙色按钮样式 */
.btn-zhengming {
  background-color: #ffe0b2;
  border-color: #fb8c00;
}

/* 文件上传区域 */
.wenjian {
  width: 90%;
  /* 宽度占满90% */
  max-width: 400px;
  /* 最大宽度限制 */
  margin-top: 0;
  margin-left: 0;
  /* 取消左边距，由父容器居中 */
  background-color: #bbdefb;
}

/* 说明文字区域 */
.shuoming {
  width: 90%;
  /* 宽度占满90% */
  max-width: 400px;
  /* 最大宽度限制 */
  margin-top: 16px;
  margin-left: 0;
  /* 取消左边距，由父容器居中 */
  padding: 0 16px;
  /* 左右内边距，避免文字贴边 */
  box-sizing: border-box;
}

.solid {
  border: 1px solid #ccc;
  padding: 12px;
  border-radius: 4px;
  color: black;
  text-align: left;
  font-size: 14px;
  line-height: 1.6;
  /* 行高增加，便于阅读 */
}

/* 提交按钮容器 */
.submit-btn-container {
  width: 90%;
  /* 宽度占满90% */
  max-width: 400px;
  /* 最大宽度限制 */
  margin-top: 24px;
  margin-left: 0;
  /* 取消左边距，由父容器居中 */
  padding: 0;
}

/* 提交按钮 */
.submit-btn {
  width: 100%;
  height: 48px;
  /* 高度增加，手指点击区域更大 */
  font-size: 16px;
  background-color: green;
}

/* 上传提示文字 */
.upload-hint {
  font-size: 12px;
  color: #999;
  margin-top: 8px;
  text-align: center;
}

/* 字段集样式调整 */
fieldset {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 16px;
  box-sizing: border-box;
}

legend {
  font-size: 15px;
  color: #666;
  padding: 0 8px;
}

/* 完成标识样式 */
.completed-tag {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  background-color: #52c41a;
  color: white;
  font-size: 12px;
  border-radius: 12px;
}

/* 退出登录按钮样式 */
.logout-btn {
  width: 100%;
  height: 48px;
  font-size: 16px;
  border: 1px solid #dcdfe6;
}

/* 上传成功反馈样式 */
.upload-success-feedback {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 12px;
  color: #52c41a;
  font-size: 14px;
}

.success-icon {
  margin-right: 8px;
  font-size: 16px;
}

/* 预览图片样式（补充，避免图片过大） */
.preview-image {
  width: 80px;
  height: 80px;
  margin-left: 12px;
  object-fit: cover;
  border-radius: 4px;
}
</style>